import { useState } from "react"
import { ref, uploadBytes, type FirebaseStorage } from "firebase/storage"
import { FloatingLabel, Form } from "react-bootstrap"
import { type Control, type FieldPath, type FieldValues, useController, useFormContext, type UseFormTrigger } from "react-hook-form"

export const ManagedFireStorageUpload = <TFieldValues extends FieldValues>(p: {
  storage: FirebaseStorage
  control: Control<TFieldValues>
  name: FieldPath<TFieldValues>
  label: string
  uploadPath: string
  disabled?: boolean
  includeFileNameInUploadPath?: boolean
  accept: string
  required?: boolean
  helper?: string
  setAsBoolean?: boolean
  resolveValue?: (uploadPath: string, fileName: string) => string
  onUploaded?: (file: File) => void
  trigger?: UseFormTrigger<TFieldValues>
}) => {
  const [isUploading, setIsUploading] = useState(false)
  const { field, fieldState } = useController({ name: p.name, control: p.control })
  const formContext = useFormContext<TFieldValues>()
  const trigger = p.trigger ?? formContext?.trigger

  const resolveFieldValue = (uploadPath: string, fileName: string): unknown => {
    if (p.setAsBoolean) return true
    if (p.resolveValue) return p.resolveValue(uploadPath, fileName)
    return fileName
  }

  const resolveEmptyValue = (): unknown => {
    if (p.setAsBoolean) return false
    return ""
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.item(0)
    if (file === undefined || file === null) {
      field.onChange(resolveEmptyValue())
      if (trigger) {
        await trigger(p.name)
      }
      return
    }

    setIsUploading(true)
    const reader = new FileReader()
    reader.addEventListener(
      "load",
      async () => {
        if (reader.result === null) {
          alert("file reading failed!")
          field.onChange(resolveEmptyValue())
          setIsUploading(false)
          if (trigger) {
            await trigger(p.name)
          }
          return
        }
        const uploadPath = p.includeFileNameInUploadPath ? `${p.uploadPath}/${file.name}` : p.uploadPath
        await uploadBytes(ref(p.storage, uploadPath), reader.result as ArrayBuffer, { contentType: file.type }).then(async () => {
          field.onChange(resolveFieldValue(uploadPath, file.name))
          setIsUploading(false)
          p.onUploaded?.(file)
          if (trigger) {
            await trigger(p.name)
          }
        }).catch(async (e) => {
          alert("file too large or incompatible type!")
          field.onChange(resolveEmptyValue())
          setIsUploading(false)
          console.error(e)
          if (trigger) {
            await trigger(p.name)
          }
        })
      },
      false,
    )
    reader.readAsArrayBuffer(file)
  }

  return (
    <>
      <FloatingLabel
        label={p.required ? `${p.label} *` : p.label}
        className="mb-3"
      >
        <Form.Control
          type="file"
          disabled={p.disabled || isUploading}
          accept={p.accept}
          onChange={handleFileChange}
          isInvalid={!!fieldState.error}
        />
        {fieldState.error && (
          <Form.Control.Feedback type="invalid">
            {fieldState.error.message}
          </Form.Control.Feedback>
        )}
      </FloatingLabel>
      {p.helper && <Form.Text className="text-muted">{p.helper}</Form.Text>}
      {field.value && !p.setAsBoolean && <p className="badge bg-secondary">アップロード済み: {field.value}</p>}
      {field.value && p.setAsBoolean && <p className="badge bg-secondary">アップロード済み</p>}
      {isUploading && <p className="text-muted">アップロード中...</p>}
    </>
  )
}
